<script lang="ts">
	//Hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	//Services
	import { format } from '../../../../services/i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { number_to_letter } from '../../../../helpers/utilities';
	import { ONE_CHANNEL_GEN_1_DEVICES } from 'src/constants';
	//Configs
	import { devices_config } from '@devices/index';

	//Design system components
	import { Button, Input, Select, Text, toast } from '@Shelly/shelly-design-system';
	import SyncName from '../../ApplicationSettings/SyncName.svelte';

	export let device;

	let channel = 0 /*for SHEM-3 only*/,
		phase,
		mode,
		options = []; /*for SHEM-3 only*/

	const id = real_id(device.id);

	let localIp = get_local_ip(id);
	$: synced = device.name_sync;
	let loading = false;

	$: statusStore = getDeviceStatus(id);
	$: status = $statusStore;
	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	const category = device.category !== 'inputs_reader' ? device.category + 's' : 'inputs';
	let name = settings[category][device.type !== 'SHEM-3' ? device.channel : channel].name
		? settings[category][device.type !== 'SHEM-3' ? device.channel : channel].name
		: device.name || '';
	let cache = name;

	beforeUpdate(() => {
		if (
			status?.online &&
			typeof settings !== 'undefined' &&
			typeof devices_config[device.type] !== 'undefined'
		) {
			mode =
				typeof status.mode !== 'undefined'
					? status.mode
					: settings.mode
						? settings.mode
						: devices_config[device.type].family
							? devices_config[device.type].family
							: '';
		}
	});

	onMount(() => {
		if (device.type === 'SHEM-3') {
			for (let i = 0; i < devices_config[$devices[device.id].type].modes[mode].phases; i++) {
				options.push($format(`label_phase_${number_to_letter(i, true).toLowerCase()}`));
			}
			phase = options[0];
		}
	});

	const changeName = async () => {
		name = name.trim();
		if (cache !== name && name !== '') {
			let res;
			loading = true;
			//save to local ui
			if (synced) {
				if (localIp) {
					res = await localReq(device, `${localIp}/settings`, 'post', {
						name: name,
						channel: device.type !== 'SHEM-3' ? device.channel : channel,
					});
				} else {
					res = await postReq(`/device/rename`, {
						id: id,
						name: name.length > 0 ? name : 'null',
						channel: device.type !== 'SHEM-3' ? device.channel : channel,
					});
				}

				if (res.isok) {
					cache = name;
					toast.open({
						type: 'success',
						title:
							$format('chan_name_succ', { values: { name: name } }) ||
							`Channel name was changed to ${name}`,
					});
				} else {
					toast.open({
						type: 'danger',
						title: $format('error_global'),
					});
				}
			}

			settings[category][device.type !== 'SHEM-3' ? device.channel : channel].name = name;

			if (device.type !== 'SHEM-3' && !ONE_CHANNEL_GEN_1_DEVICES.includes(device.type)) {
				device.name = name;
				devices.update(device);
				let int_res = await postReq('interface/device/save', {
					id: device.id,
					data: JSON.stringify(device),
				});

				if (int_res.isok) {
					cache = name;
					devices_settings.update_devices_settings(id, settings);
					toast.open({
						type: 'success',
						title:
							$format('chan_name_succ', { values: { name: name } }) ||
							`Channel name was changed to ${name}`,
					});
				} else {
					toast.open({
						type: 'danger',
						title: $format('error_global'),
					});
				}
			} else {
				res = await postReq(`/device/rename`, {
					id: id,
					name: name.length > 0 ? name : 'null',
					channel: device.type !== 'SHEM-3' ? device.channel : channel,
				});

				if (res.isok) {
					cache = name;
					devices_settings.update_devices_settings(id, settings);
					toast.open({
						type: 'success',
						title:
							$format('chan_name_succ', { values: { name: name } }) ||
							`Channel name was changed to ${name}`,
					});
				} else {
					toast.open({
						type: 'danger',
						title: $format('error_global'),
					});
				}
			}
		}

		loading = false;
	};

	const handlePhaseChange = (e) => {
		channel = options.indexOf(e.detail);
		name = settings[category][channel].name || '';
		cache = name;
	};
</script>

{#if device.type === 'SHEM-3'}
	<Select
		size="m"
		class="mb-s"
		bind:value={phase}
		{options}
		icon={'fas fa-microchip'}
		on:change={handlePhaseChange}
		id="select-21cdcpv"
		aria-label={$format('label_phase', { default: 'Phase' })}
	/>
{/if}
<Input
	size="m"
	class="mb-s"
	icon="null"
	label={$format('sub_settings_title_channel_name')}
	bind:value={name}
	on:enter={changeName}
	id="input-1reqzyr"
	aria-labelledby="input-1reqzyr"
/>

{#if device.channels_count > 1}
	<SyncName
		nameType="channel"
		{device}
		info={$format('channel_rename_description', {
			default:
				'Keep the channel name in sync with the name given in the application. The sync is only from the application to the device and not vice-versa.',
		})}
	/>
{:else}
	<Text class="mt-s mb-s" styleType="body-small">
		{$format('channel_rename_description_in_settings', {
			default:
				'Change the current channel name. This will reflect on the channel name in the settings.',
		})}
	</Text>
{/if}
<Button
	class="mt-s"
	{loading}
	size="m"
	width="full"
	on:click={changeName}
	disabled={name && (name?.length <= 0 || cache === name)}>{$format('button_save')}</Button
>
